if (!customElements.get('product-buy-bar')) {
  class ProductBuyBar extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      if (!this.form) return;
      this.section = this.form.closest('.js-product');
      this.picker = this.section.querySelector('variant-picker');
      this.product = JSON.parse(this.querySelector('[data-product]').textContent);
      this.quantity = this.querySelector('[data-quantity]');
      this.mainQuantity = this.form.querySelector('[name="quantity"]');
      this.buy = this.querySelector('[data-buy]');
      this.buyLabel = this.buy.textContent;
      this.error = this.querySelector('[data-error]');
      this.options = Array.from(this.querySelectorAll('[data-option-index]'));
      this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: this.dataset.currency || 'USD'
      });
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-option-index]')) this.selectOption(event.target);
        if (event.target === this.quantity) this.setQuantity(this.quantity.value);
      }, { signal });
      this.addEventListener('click', (event) => {
        const step = event.target.closest('[data-step]');
        if (step) this.setQuantity(Number(this.quantity.value) + Number(step.dataset.step));
        if (event.target.closest('[data-buy]')) this.checkout();
      }, { signal });
      this.section.addEventListener('change', () => this.render(), { signal });
      this.section.addEventListener('on:variant:change', () => this.render(), { signal });
      this.section.addEventListener('on:bundle:change', () => this.render(), { signal });
      this.hidden = false;
      this.resizeObserver = new ResizeObserver(() => {
        document.documentElement.style.setProperty('--product-buy-bar-height', `${this.offsetHeight}px`);
      });
      this.resizeObserver.observe(this);
      this.render();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      document.documentElement.style.removeProperty('--product-buy-bar-height');
    }

    getVariant() {
      if (this.picker) return this.picker.getSelectedVariant();
      return this.product.variants.find((variant) => String(variant.id) === this.form.elements.id.value);
    }

    selectOption(select) {
      const index = Number(select.dataset.optionIndex);
      const selector = this.picker?.optionSelectors[index];
      if (!selector) return;
      if (selector.dataset.selectorType === 'dropdown') {
        const custom = selector.querySelector('custom-select');
        const option = Array.from(selector.querySelectorAll('.js-option')).find((item) => item.dataset.value === select.value);
        if (custom && option) {
          const { scrollX, scrollY } = window;
          custom.selectOption(option);
          select.focus({ preventScroll: true });
          // The theme's custom select focuses its original button on close.
          window.scrollTo({ left: scrollX, top: scrollY, behavior: 'instant' });
        }
      } else {
        const input = Array.from(selector.querySelectorAll('input')).find((item) => item.value === select.value);
        if (input) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      this.render();
    }

    setQuantity(value) {
      const min = Math.max(1, Number(this.mainQuantity.min) || 1);
      const max = Number(this.mainQuantity.max) || Infinity;
      const quantity = Math.min(max, Math.max(min, Math.floor(Number(value) || min)));
      this.mainQuantity.value = quantity;
      this.mainQuantity.dispatchEvent(new Event('change', { bubbles: true }));
      this.render();
    }

    render() {
      const variant = this.getVariant();
      const selected = this.picker?.getSelectedOptions() || variant?.options || [];
      this.options.forEach((select, index) => { select.value = selected[index] || ''; });
      const quantity = Math.max(1, Math.floor(Number(this.mainQuantity.value) || 1));
      this.quantity.value = quantity;
      this.quantity.min = this.mainQuantity.min || 1;
      this.quantity.max = this.mainQuantity.max;
      this.querySelector('[data-step="-1"]').disabled = quantity <= Number(this.quantity.min);
      this.querySelector('[data-step="1"]').disabled = !!this.quantity.max && quantity >= Number(this.quantity.max);
      let price = variant?.price || 0;
      let compare = Math.max(price, variant?.compare_at_price || 0);
      const addonIds = new Set();
      this.section.querySelectorAll('accessories-bundle').forEach((bundle) => {
        const totals = bundle.getTotals();
        price += totals.price;
        compare += totals.compare;
        bundle.updateQuantity(quantity);
        bundle.querySelectorAll('.accessory').forEach((item) => {
          if (item.dataset.variantid && item.querySelector('.accessory-checkbox:checked')) {
            addonIds.add(item.dataset.variantid);
          }
        });
      });
      for (const [giftId, variants] of Object.entries(window.bundleGiftJson || {})) {
        if (variants.includes(String(variant?.id))) addonIds.add(giftId);
      }
      const giftCount = this.querySelector('[data-gift-count]');
      giftCount.hidden = !variant || addonIds.size === 0;
      giftCount.textContent = `+ Add-ons × ${addonIds.size * quantity}`;
      this.querySelector('[data-price]').textContent = variant ? this.formatter.format(price * quantity / 100) : '—';
      const was = this.querySelector('[data-compare]');
      const saving = this.querySelector('[data-saving]');
      was.hidden = saving.hidden = !variant || compare <= price;
      was.textContent = this.formatter.format(compare * quantity / 100);
      saving.textContent = `${compare > 0 ? Math.round((compare - price) / compare * 100) : 0}%`;
      const image = this.querySelector('[data-product-image]');
      const src = variant?.featured_image?.src || this.product.featured_image;
      if (image && src) image.src = src;
      this.buy.disabled = this.busy || !variant?.available;
      this.buy.textContent = !variant ? theme.strings.noSelectedVariant : !variant.available ? theme.strings.noStock : this.buyLabel;
    }

    async checkout() {
      if (this.busy || !this.getVariant()?.available) return;
      this.setQuantity(this.quantity.value);
      if (!this.form.reportValidity()) return;
      this.busy = true;
      this.error.hidden = true;
      this.buy.setAttribute('aria-busy', 'true');
      this.render();
      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: this.form.closest('product-form').getItems() })
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || theme.strings.cartError);
        window.location.assign(this.dataset.checkoutUrl);
      } catch (error) {
        this.error.textContent = error.message || theme.strings.cartError;
        this.error.hidden = false;
        this.busy = false;
        this.buy.removeAttribute('aria-busy');
        this.render();
      }
    }
  }
  customElements.define('product-buy-bar', ProductBuyBar);
}
